// A quiet, folded list at the end of the feed: what is snoozed and when each card comes back,
// with "Bring back now" (Undo snoozes it again until the same moment). No count; nothing shows
// when nothing is snoozed. Times read in the browser's zone, which setup saved as the owner's.
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import type { CardSummary } from "@shared/api";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { acceptCard, feedKeys, useSnoozedCards } from "@web/lib/feedQueries";
import { formatFull } from "@web/lib/time";
import { registerUndo } from "@web/lib/undoRegistry";
import { useNow } from "@web/lib/useNow";
import { Button } from "@web/ui/Button";
import { restoreCard } from "./cardOps";
import { backLabel, bringBack } from "./snoozed";

export function SnoozedTray({ feedId }: { feedId: string }) {
  const queryClient = useQueryClient();
  const snoozed = useSnoozedCards(feedId);
  const now = useNow(60_000);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const cards = snoozed.data ?? [];
  if (!cards.length) return null;

  const bringBackNow = async (card: CardSummary) => {
    setBusyId(card.id);
    try {
      const undo = await bringBack(card, {
        restore: () => restoreCard(queryClient, card.id),
        snoozeUntil: async (until) => acceptCard(queryClient, await feedApi.snooze(card.id, until)),
      });
      queryClient.setQueryData<CardSummary[]>(feedKeys.snoozed(feedId), (list) =>
        list?.filter((c) => c.id !== card.id),
      );
      registerUndo({
        message: `Brought back · ${short(card.title)}`,
        undo: async () => {
          const result = await undo();
          void queryClient.invalidateQueries({ queryKey: feedKeys.snoozed(feedId) });
          return result;
        },
        doneMessage: (result) =>
          result === "snoozed"
            ? `Snoozed again. ${backLabel(card.snoozedUntil, Date.now())}.`
            : "It was due back by now, so it stays.",
        tooLateMessage: "It changed since, so it stays back.",
      });
    } catch {
      toast("Couldn’t bring that back just now. It’s still snoozed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section aria-label="Snoozed" className="px-4 pb-10 md:px-5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-md py-1 text-sm text-muted hover:text-ink"
      >
        <ChevronRight
          aria-hidden
          className={cn("size-4 transition-transform", open && "rotate-90")}
        />
        Snoozed
      </button>
      {open ? (
        <ul className="mt-2 flex flex-col divide-y divide-line overflow-hidden rounded-xl bg-paper ring-1 ring-line">
          {cards.map((card) => (
            <li key={card.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <Link
                  to="/feed/cards/$cardId"
                  params={{ cardId: card.id }}
                  className="block truncate text-ink hover:underline"
                >
                  {card.title}
                </Link>
                <p
                  className="text-sm text-muted"
                  title={card.snoozedUntil ? formatFull(card.snoozedUntil) : undefined}
                >
                  {backLabel(card.snoozedUntil, now)}
                </p>
              </div>
              <Button
                size="sm"
                className="shrink-0"
                busy={busyId === card.id}
                disabled={!!busyId && busyId !== card.id}
                onClick={() => void bringBackNow(card)}
              >
                Bring back now
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function short(title: string): string {
  return title.length > 48 ? `${title.slice(0, 45)}…` : title;
}
